import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import { OrderForm } from "../../order/edit/components/OrderForm";
import { ShipForMeForm } from "../../shipforme/edit/components/ShipForMeForm";

interface OrderGroupItemEditorProps {
  itemId: number;
  /** 0 = buy for me, 1 = ship for me. */
  orderType: number;
  /** Without the item's update permission the inputs are shown but locked. */
  canEdit: boolean;
  /** How the group is paid; the group owns it and the item only carries it. */
  paymentType: number | null;
  /** Who ships the group; the group owns it and the item only carries it. */
  shippingCompanyId: number | null;
  /** Economy or fast; the group owns it and the item only carries it. */
  deliveryMode: number | null;
  /** The route of the group; the item only carries it. */
  originCountryId: number | null;
  destinationCountryId: number | null;
  cityId: number | null;
  /** The price per LB agreed for the whole group, or null when the price table of the shipping company applies. */
  pricePerLB: number | null;
  /** Called after every saved change so the group can be reloaded. */
  onChanged: () => Promise<unknown> | void;
}

const NUMERIC_FIELDS = [
  "initialPartialPayment",
  "quantity",
  "itemPrice",
  "merchentDeliveryFee",
  "merchentDeliveryFeeInBaseCurrency",
  "width",
  "height",
  "length",
  "weight",
  "shippingCompanyPricePerLB"
];
// Every lookup (…Id), the payment and item types and the delivery mode are numbers on the server.
const isNumericField = (name: string) =>
  NUMERIC_FIELDS.includes(name) || name.endsWith("Id") || name.includes("Type") || name === "deliveryMode";

const STATUS_PENDING = 0;
const STATUS_REJECTED = 2;

/**
 * The inputs of one item, edited inside its order group: saving, calculating,
 * rejecting and resetting the item, then bringing the group in line.
 */
export const OrderGroupItemEditor: React.FC<OrderGroupItemEditorProps> = ({ itemId, orderType, canEdit, paymentType, shippingCompanyId, deliveryMode, originCountryId, destinationCountryId, cityId, pricePerLB, onChanged }) => {
  const { t, i18n } = useTranslation("common");
  const isShipForMe = orderType === 1;
  const controller = isShipForMe ? "adminshipforme" : "adminorder";

  const [itemData, setFormData] = useState<any>(null);
  // What belongs to the group is always taken from the group: payment type, shipping company,
  // delivery mode, route and price per LB.
  const formData = useMemo(
    () => (itemData
      ? {
        ...itemData,
        paymentType,
        shippingCompanyId,
        deliveryMode,
        originCountryId,
        destinationCountryId,
        cityId,
        shippingCompanyPricePerLB: pricePerLB
      }
      : null),
    [itemData, paymentType, shippingCompanyId, deliveryMode, originCountryId, destinationCountryId, cityId, pricePerLB]);
  const termsChosen = paymentType !== null && shippingCompanyId !== null && deliveryMode !== null
    && originCountryId !== null && destinationCountryId !== null && cityId !== null;
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [shippingCompanies, setShippingCompanies] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [weightUnits, setWeightUnits] = useState<any[]>([]);
  const [lengthUnits, setLengthUnits] = useState<any[]>([]);
  const [isLBPriceEnabled, setIsLBPriceEnabled] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadItem = useCallback(async () => {
    try {
      const response = await adminApi.call(`/${controller}/${itemId}`, "GET");
      setFormData(response.success && response.data?.order ? response.data.order : null);
    } catch (error) {
      console.error("Failed to load order group item", error);
      setFormData(null);
    } finally {
      setLoading(false);
    }
  }, [controller, itemId]);

  useEffect(() => {
    loadItem();
  }, [loadItem, i18n.language]);

  useEffect(() => {
    let cancelled = false;
    const loadList = async (path: string, pick: (data: any) => any[] | undefined) => {
      try {
        const response = await adminApi.call(path, "GET");
        return (response.success && pick(response.data)) || [];
      } catch (error) {
        console.error(`Failed to load ${path}`, error);
        return [];
      }
    };
    const items = (data: any) => data?.items;
    const list = (data: any) => (Array.isArray(data) ? data : undefined);

    const loadLookups = async () => {
      const [countryList, currencyList, cityList, categoryList, paymentMethodList, weightList, lengthList] = await Promise.all([
        loadList("/country", items),
        loadList(isShipForMe ? "/currency" : "/currency/GetBothAndSourceCurrencies?pageSize=1000000", items),
        loadList("/city?page=1&pageSize=1000", items),
        loadList("/category?pageSize=10000", items),
        isShipForMe ? Promise.resolve([]) : loadList("/paymentmethod?pageSize=10000", items),
        loadList("/measurementunit/by-type/1", list),
        loadList("/measurementunit/by-type/0", list)
      ]);
      if (cancelled) return;
      setCountries(countryList.filter((country: any) => !country.isSuspended));
      setCurrencies(currencyList);
      setCities(cityList);
      setCategories(categoryList);
      setPaymentMethods(paymentMethodList);
      setWeightUnits(weightList);
      setLengthUnits(lengthList);
    };

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [isShipForMe, i18n.language]);

  // The shipping companies that serve the route of the group.
  useEffect(() => {
    if (!originCountryId || !destinationCountryId) return;

    let cancelled = false;
    const loadShippingCompanies = async () => {
      const params = new URLSearchParams({
        pageSize: "10000",
        isActive: "true",
        sourceCountryId: String(originCountryId),
        destinationCountryId: String(destinationCountryId)
      });
      try {
        const response = await adminApi.call(`/shippingCompany?${params.toString()}`, "GET");
        if (!cancelled) setShippingCompanies((response.success && response.data?.items) || []);
      } catch (error) {
        console.error("Failed to load shipping companies", error);
      }
    };

    loadShippingCompanies();
    return () => {
      cancelled = true;
    };
  }, [originCountryId, destinationCountryId]);

  const handleInputChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous: any) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : isNumericField(name) ? Number(value) : value
    }));
  };

  const handleQuantityChange = (increment: number) => {
    setFormData((previous: any) => ({ ...previous, quantity: Math.max(1, previous.quantity + increment) }));
  };

  /** Runs a write request, reports the outcome, then reloads the item and its group. */
  const submit = async (path: string, body: unknown, successMessage: string, setBusy: (busy: boolean) => void) => {
    // Nothing of an item is saved before its group has its payment type, shipping company and delivery mode.
    if (!termsChosen) return;
    setBusy(true);
    try {
      const response = await adminApi.call(path, "POST", body);
      if (!response.success) {
        showErrorToast(response.error || t("orderGroups.messages.actionFailed"));
        return;
      }
      showSuccessToast(successMessage);
      await loadItem();
      await onChanged();
    } catch (error) {
      console.error("Order group item action failed", error);
      showErrorToast(t("orderGroups.messages.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const saveItem = () =>
    submit(`/${controller}/validate`, formData, t("orderGroups.itemEditor.saved"), setUpdating);

  const calculateItem = () =>
    submit(`/${controller}/calculate/${itemId}`, undefined, t("orderGroups.itemEditor.calculated"), setCalculating);

  const resetItem = () =>
    submit(`/${controller}/updatestatus`, { orderId: itemId, orderStatus: STATUS_PENDING, comment: "" }, t("orderGroups.itemEditor.reset"), setResetting);

  const changeStatus = async (status: number, comment?: string) => {
    const note = (comment || "").trim();
    if (formData.orderStatus === STATUS_PENDING && status === STATUS_REJECTED && !note) {
      showErrorToast(t("orderGroups.itemEditor.rejectNoteRequired"));
      return;
    }
    await submit(`/${controller}/updatestatus`, { orderId: itemId, orderStatus: status, comment: note }, t("orderGroups.messages.statusChanged"), setUpdating);
  };

  if (loading) {
    return <div className="text-muted py-3">{t("orderGroups.itemEditor.loading")}</div>;
  }

  if (!formData) {
    return <div className="alert alert-warning mb-0">{t("orderGroups.itemEditor.loadError")}</div>;
  }

  const formProps = {
    formData,
    countries,
    currencies,
    categories,
    shippingCompanys: shippingCompanies,
    cities,
    weightMeasurementUnits: weightUnits,
    lengthMeasurementUnits: lengthUnits,
    isLBPriceEnabled,
    setIsLBPriceEnabled,
    handleInputChange,
    handleQuantityChange,
    updateOrder: saveItem,
    calculateOrder: calculateItem,
    resetOrder: resetItem,
    handleStatusUpdate: changeStatus,
    updating,
    calculatingOrder: calculating,
    resettingOrder: resetting,
    id: String(itemId)
  };

  return (
    <fieldset className="order-group-item-editor" disabled={!canEdit || !termsChosen} aria-label={t("orderGroups.itemEditor.title")}>
      {!canEdit && <div className="alert alert-secondary">{t("orderGroups.itemEditor.readOnly")}</div>}
      {canEdit && !termsChosen && <div className="alert alert-warning">{t("orderGroups.itemEditor.termsMissing")}</div>}
      {isShipForMe
        ? <ShipForMeForm {...formProps} groupOwnsTerms />
        : <OrderForm {...formProps} paymentMethods={paymentMethods} groupOwnsTerms />}
    </fieldset>
  );
};

export default OrderGroupItemEditor;
